import { useRef, useCallback } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import { arrowRightSharpIcon } from '../../assets/index'
import { MOBILE_MQ, mobilePaddingX, mobileSectionTitleSx } from '../../theme/mobileLayout'
import { BUTTON_THEME_COLOR, BUTTON_TRANSITION } from '../../theme/buttonHover'
import { scale } from '../../theme/scale'
import { SLIDER_TRANSITION_SPEED } from '../../theme/slider'
import 'swiper/css'

const SLIDE_WIDTH = 440
const SLIDE_GAP = 40
const MOBILE_SLIDE_GAP = 20
const CARD_SHADOW_BLEED = 8
const CARD_SHADOW = '0px 0px 8px 0px rgba(0,0,0,0.12)'
const CARD_SHADOW_HOVER = '0px 4px 20px 0px rgba(0,0,0,0.16)'

function ExperienceNavButton({ direction, onClick, size = 45 }) {
  return (
    <IconButton
      onClick={onClick}
      aria-label={direction === 'prev' ? 'Previous slide' : 'Next slide'}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        p: 0,
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#fffefa',
        border: `1px solid ${BUTTON_THEME_COLOR}`,
        transition: BUTTON_TRANSITION,
      }}
    >
      <Box
        component="img"
        src={arrowRightSharpIcon}
        alt=""
        sx={{
          width: 24,
          height: 24,
          display: 'block',
          transform: direction === 'prev' ? 'rotate(180deg)' : 'none',
        }}
      />
    </IconButton>
  )
}

function ProgramExperienceCard({ name, role, quote, avatar, roleLines }) {
  const useTallAvatar = Boolean(roleLines)

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: { xs: 'auto', lg: 422 },
        bgcolor: '#fffefa',
        borderRadius: '20px',
        boxShadow: CARD_SHADOW,
        p: '24px',
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        '&:hover': {
          boxShadow: CARD_SHADOW_HOVER,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '40px', lg: '100px' },
          [MOBILE_MQ]: { gap: '40px' },
          flex: 1,
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Box
            sx={{
              position: 'relative',
              width: { xs: 48, lg: 88 },
              height: { xs: 48, lg: 88 },
              [MOBILE_MQ]: { width: 48, height: 48 },
              borderRadius: '50%',
              flexShrink: 0,
              overflow: 'hidden',
              bgcolor: '#e0e0e0',
            }}
          >
            <Box
              component="img"
              src={avatar}
              alt={name}
              sx={{
                position: 'absolute',
                width: '100%',
                height: useTallAvatar ? '150%' : '100%',
                left: 0,
                top: useTallAvatar ? '-2.27%' : 0,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: roleLines ? { xs: '2px', lg: '6px' } : { xs: '6px', lg: '6px' },
              [MOBILE_MQ]: { gap: roleLines ? '2px' : '6px' },
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 18, lg: 22 },
                lineHeight: 'normal',
                fontWeight: 400,
                color: '#111',
                [MOBILE_MQ]: { fontSize: 18 },
              }}
            >
              {name}
            </Typography>
            {roleLines ? (
              <Box sx={{ opacity: 0.6 }}>
                {roleLines.map((line) => (
                  <Typography
                    key={line}
                    sx={{
                      fontSize: { xs: 12, lg: 20 },
                      lineHeight: 'normal',
                      fontWeight: 400,
                      color: '#111',
                      [MOBILE_MQ]: { fontSize: 12 },
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: 12, lg: 20 },
                  lineHeight: 'normal',
                  fontWeight: 400,
                  color: '#111',
                  opacity: 0.6,
                  [MOBILE_MQ]: { fontSize: 12 },
                }}
              >
                {role}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 20, lg: 22 },
            lineHeight: 'normal',
            fontWeight: 400,
            color: '#111',
            [MOBILE_MQ]: { fontSize: 20 },
          }}
        >
          {quote}
        </Typography>
      </Box>
    </Box>
  )
}

export default function ProgramTestimonialsSection({ testimonials = [], compactBottom = false }) {
  const swiperRef = useRef(null)
  const slides = [...testimonials, ...testimonials]

  const handleSwiper = useCallback((swiper) => {
    swiperRef.current = swiper
    requestAnimationFrame(() => swiper.autoplay?.start())
  }, [])

  const slidePrev = useCallback(() => {
    swiperRef.current?.slidePrev()
  }, [])

  const slideNext = useCallback(() => {
    swiperRef.current?.slideNext()
  }, [])

  if (!testimonials.length) return null

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1920,
        mx: 'auto',
        pl: mobilePaddingX,
        pr: 0,
        pb: compactBottom ? { xs: 0, sm: 0, lg: 0 } : { xs: 8, sm: 10, lg: '150px' },
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '32px', md: '80px' },
          [MOBILE_MQ]: { gap: '32px' },
        }}
      >
        <Box
          sx={{
            maxWidth: 1520,
            width: '100%',
            pr: mobilePaddingX,
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 3, sm: 2 },
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...mobileSectionTitleSx,
              fontSize: { ...mobileSectionTitleSx.fontSize, lg: scale.fontSize.sectionTitle },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              mb: 0,
              [MOBILE_MQ]: { fontSize: 28 },
            }}
          >
            Experiences That Inspire
          </Typography>

          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
            }}
          >
            <ExperienceNavButton direction="prev" onClick={slidePrev} />
            <ExperienceNavButton direction="next" onClick={slideNext} />
          </Box>
        </Box>

        <Box
          sx={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 48px)',
              md: 'calc(100vw - 80px)',
              lg: 'calc(100vw - 160px)',
              xl: 'calc(100vw - 400px)',
            },
            maxWidth: 'none',
            overflow: 'visible',
            position: 'relative',
            '& .swiper': { overflow: 'visible', width: '100%' },
            '& .swiper-wrapper': { alignItems: 'stretch' },
            '& .swiper-slide': {
              width: {
                xs: 'calc(100vw - 40px) !important',
                sm: '360px !important',
                md: '400px !important',
                lg: `${SLIDE_WIDTH + CARD_SHADOW_BLEED * 2}px !important`,
              },
              height: 'auto',
              flexShrink: 0,
              boxSizing: 'border-box',
              padding: `${CARD_SHADOW_BLEED}px`,
              overflow: 'visible',
              [MOBILE_MQ]: {
                width: 'calc(100vw - 40px) !important',
              },
            },
          }}
        >
          <Swiper
            modules={[Autoplay]}
            onSwiper={handleSwiper}
            slidesPerView="auto"
            spaceBetween={MOBILE_SLIDE_GAP}
            loop
            loopAdditionalSlides={testimonials.length}
            loopPreventsSliding={false}
            observer
            observeParents
            resizeObserver
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              waitForTransition: true,
            }}
            speed={SLIDER_TRANSITION_SPEED}
            grabCursor
            breakpoints={{
              426: { spaceBetween: 24 },
              900: { spaceBetween: 32 },
              1200: { spaceBetween: SLIDE_GAP },
            }}
          >
            {slides.map((item, index) => (
              <SwiperSlide key={`${item.name}-${index}`}>
                <ProgramExperienceCard {...item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'center',
            pr: mobilePaddingX,
            boxSizing: 'border-box',
            gap: '10px',
          }}
        >
          <ExperienceNavButton direction="prev" onClick={slidePrev} size={40} />
          <ExperienceNavButton direction="next" onClick={slideNext} size={40} />
        </Box>
      </Box>
    </Box>
  )
}
